import { MINIMUM_SDK_AUTO_UPDATE_VERSION, MINIMUM_SDK_VERSION } from '@/const';

export { MINIMUM_SDK_AUTO_UPDATE_VERSION, MINIMUM_SDK_VERSION };

export type SdkCompatibilityMode =
  | 'compatible'
  | 'updateRequiredWaiting'
  | 'manualUpdateRequired'
  | 'compatibilityUnknown';

/**
 * Compare two semantic version strings.
 * @param version1 First version string (e.g., '0.22.15')
 * @param version2 Second version string (e.g., '0.22.16')
 * @returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 */
export const compareVersions = (version1: string, version2: string): number => {
  const v1Parts = version1.split('.').map(part => Number(part) || 0);
  const v2Parts = version2.split('.').map(part => Number(part) || 0);

  const maxLength = Math.max(v1Parts.length, v2Parts.length);

  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;

    if (v1Part < v2Part) return -1;
    if (v1Part > v2Part) return 1;
  }

  return 0;
};

/**
 * Check if the current SDK version meets the minimum requirement.
 * @param currentVersion Current SDK version string
 * @param minimumVersion Minimum required version string (default: MINIMUM_SDK_VERSION)
 * @returns true if current version is sufficient, false otherwise
 */
export const isVersionSufficient = (currentVersion: string, minimumVersion: string = MINIMUM_SDK_VERSION): boolean => {
  return compareVersions(currentVersion, minimumVersion) >= 0;
};

/**
 * Check if the current SDK version can still use in-app updater recovery.
 * @param currentVersion Current SDK version string
 * @param minimumAutoUpdateVersion Minimum version that supports updater recovery
 * @returns true if updater recovery should be attempted, false for manual-only
 */
export const supportsAutoUpdateRecovery = (
  currentVersion: string,
  minimumAutoUpdateVersion: string = MINIMUM_SDK_AUTO_UPDATE_VERSION
): boolean => {
  return compareVersions(currentVersion, minimumAutoUpdateVersion) >= 0;
};

/**
 * Resolve the expected SDK compatibility mode from version thresholds.
 * @param currentVersion Current SDK version string
 * @returns Compatibility mode used by startup and UI gating.
 */
export const getSdkCompatibilityMode = (currentVersion: string): SdkCompatibilityMode => {
  if (!currentVersion || currentVersion.trim().length === 0) {
    return 'compatibilityUnknown';
  }
  if (isVersionSufficient(currentVersion)) {
    return 'compatible';
  }
  if (!supportsAutoUpdateRecovery(currentVersion)) {
    return 'manualUpdateRequired';
  }
  return 'updateRequiredWaiting';
};
